import { Page } from '@/components/layout/Page'
import { Status } from '@/components/common/Status'
import { AlertTriangleIcon } from '@/components/common/icons'

/**
 * Fallback UI for `Sentry.ErrorBoundary`. Rendered when any descendant
 * throws an unhandled error — i.e. the React tree is no longer in a
 * trustable state. Offers the only two actions that can plausibly recover:
 *   - Reload (clears the in-memory tree; most bugs go away after a fresh
 *     boot since they're state-driven).
 *   - Go to /signed-out (when the crash is auth-state-driven, starting from
 *     a clean signed-out card lets the user re-auth cleanly).
 *
 * Deliberately avoids the `useSession` / `useAuth` hooks: the crash may
 * originate inside `AuthProvider` itself, in which case calling those hooks
 * from the fallback would retrigger the same throw and loop the
 * ErrorBoundary forever. Plain `window.location` navigation sidesteps that.
 *
 * Sentry has already captured the exception by the time this renders; we
 * don't need to re-report it.
 */
export function CrashCard() {
  return (
    <Page>
      <Status
        tone="bad"
        alert
        icon={<AlertTriangleIcon />}
        title={<>Something&rsquo;s broken.</>}
        actions={
          <div className="status__actions--pair">
            <button
              type="button"
              className="btn"
              onClick={() => window.location.reload()}
            >
              Reload
            </button>
            <a className="btn" href="/signed-out">
              Sign out
            </a>
          </div>
        }
      >
        An unexpected error stopped the Lobby from rendering. We&rsquo;ve been
        notified. Reloading usually fixes it.
      </Status>
    </Page>
  )
}
